import { useAssetNames } from "@/abilities/product-and-asset/composables/asset-names";
import { useProductAndAssetAndText } from "@/abilities/product-and-asset/composables/product-and-asset-and-text";
import { useProductNames } from "@/abilities/product-and-asset/composables/product-names";
import { AccessFeature } from "@/shared/access-control/access-control";
import { useProductAndAssetSettingsAsync } from "@/shared/configurations/product-and-asset/product-and-asset-settings-async";
import { createRouteFromRouteElements } from "@/shared/environment/ability-route-helper";
import {
    Ability,
    NavigationMenuEntry,
    QualificationLevelSelectList,
    Route,
    RouteElementWithParam,
    SearchTab,
    Widget,
} from "@/shared/environment/ability.types";
import {
    CommonRouteAliases,
    CommonSearchTabAliases,
    CommonTabAliases,
} from "@/shared/environment/common-route-aliases";
import { computedText } from "@/shared/i18n/translation-types";
import { LocationQuery, RouteLocationNormalizedGeneric, RouteLocationNormalizedLoadedGeneric } from "vue-router";
import { useMoreMenuProductItems } from "../actions/product-actions";
import AssetSelectionForQualification from "../pages/asset-list-for-qualification.vue";
import AssetSearch from "../pages/asset-search.vue";
import AssetTitleSpan from "../pages/components/breadcrumb/asset-title-span.vue";
import ProductTitleSpan from "../pages/components/breadcrumb/product-title-span.vue";
import ProductSelectionForQualification from "../pages/product-list-for-qualification.vue";
import ProductSearch from "../pages/product-search.vue";
import LastAssets from "../pages/widget/last-assets.widget.vue";
import LastProducts from "../pages/widget/last-products.widget.vue";

export const abilityAlias = "product-and-asset";

export class ProductAndAssetAbility implements Ability {
    alias = abilityAlias;
    access = {
        accessFeature: AccessFeature.products,
    };

    getMenuEntries = useMoreMenuProductItems;

    getWidgets = (): Array<Widget> => [
        {
            widgetContextAlias: ["dashboard"],
            widgetComponent: LastProducts,
        },
        {
            widgetContextAlias: ["dashboard"],
            widgetComponent: LastAssets,
        },
    ];

    getNavigationMenuEntries = (): Array<NavigationMenuEntry> => [
        {
            title: computedText(useProductAndAssetAndText()),
            icon: "fa-regular fa-grid-2",
            internalRoute: {
                alias: CommonRouteAliases.productSelection,
            },
        },
    ];

    getRoutes = (): Array<Route> => [
        {
            alias: CommonRouteAliases.product,
            params: [CommonRouteAliases.product],
            getSearchParams: (currentElement: RouteElementWithParam) => {
                const result: Record<string, string> = {};

                if (currentElement.namedParams[currentElement.alias])
                    result[CommonRouteAliases.product] = currentElement.namedParams[currentElement.alias];

                return { routeParams: result, searchTab: CommonSearchTabAliases.commonArticles };
            },
            getComponent: () => import("@/abilities/product-and-asset/pages/product-detail-page.vue"),
            isRoot: true,
            getBreadCrumbs: (parentElements: Array<RouteElementWithParam>, currentElement: RouteElementWithParam) => {
                return [
                    {
                        content: {
                            componentDefinition: {
                                getComponent: () => ProductTitleSpan,
                                props: {
                                    product: currentElement.namedParams[currentElement.alias],
                                },
                            },
                        },
                        getRouterTo: () => createRouteFromRouteElements(parentElements, currentElement),
                    },
                ];
            },
        },
        {
            alias: CommonRouteAliases.asset,
            params: [CommonRouteAliases.asset],
            getSearchParams: (currentElement: RouteElementWithParam) => {
                const result: Record<string, string> = {};

                if (currentElement.namedParams[currentElement.alias])
                    result[CommonRouteAliases.asset] = currentElement.namedParams[currentElement.alias];

                return { routeParams: result, searchTab: CommonSearchTabAliases.commonArticles };
            },
            getComponent: () => import("@/abilities/product-and-asset/pages/asset-detail-page.vue"),
            parentAliases: [CommonRouteAliases.product],
            getBreadCrumbs: (parentElements: Array<RouteElementWithParam>, currentElement: RouteElementWithParam) => {
                return [
                    {
                        content: {
                            componentDefinition: {
                                getComponent: () => AssetTitleSpan,
                                props: {
                                    asset: currentElement.namedParams[currentElement.alias],
                                },
                            },
                        },
                        getRouterTo: () => createRouteFromRouteElements(parentElements, currentElement),
                    },
                ];
            },
            beforeEnter: async (to: RouteLocationNormalizedGeneric, _: RouteLocationNormalizedLoadedGeneric) => {
                const { assetsActivated } = await useProductAndAssetSettingsAsync();
                if (!assetsActivated) {
                    const params = to.params;
                    let path = to.path;
                    if (params.asset) {
                        path = to.path.replace(CommonRouteAliases.asset + "/" + params.asset, "").replace("//", "/");
                    }
                    return { path: path };
                }
            },
        },
        {
            alias: CommonRouteAliases.productSelection,
            params: [],
            isRoot: true,
            getComponent: () => import("@/abilities/product-and-asset/pages/product-selection-main.vue"),
            getBreadCrumbs: (
                parentElements: Array<RouteElementWithParam>,
                currentElement: RouteElementWithParam,
                translateMethod: (text: string) => string
            ) => {
                return [
                    {
                        content: {
                            simpleText: translateMethod("product-and-asset.Products & Assets"),
                        },
                        getRouterTo: () => createRouteFromRouteElements(parentElements, currentElement),
                    },
                ];
            },
            getSearchParams: (_currentElement: RouteElementWithParam, routeQuery: LocationQuery) => {
                //if product-selection and tab is products -> CommonSearchTabAliases.products
                const searchTab = (routeQuery?.[CommonTabAliases.search] as string) ?? CommonSearchTabAliases.products;
                return { searchTab };
            },
            beforeEnter: async (to: RouteLocationNormalizedGeneric, _: RouteLocationNormalizedLoadedGeneric) => {
                const { assetsActivated } = await useProductAndAssetSettingsAsync();
                if (!assetsActivated && to.query[CommonTabAliases.search] === CommonSearchTabAliases.assets) {
                    return { path: to.path, query: { ...to.query, [CommonTabAliases.search]: undefined } };
                }
            },
        },
    ];

    getSearchTabs: () => Array<SearchTab> = () => [
        {
            alias: CommonSearchTabAliases.products,
            title: computedText(useProductNames("plural")),
            searchComponent: {
                getComponent: () => ProductSearch,
            },
        },
        {
            alias: CommonSearchTabAliases.assets,
            title: computedText(useAssetNames("plural")),
            searchComponent: {
                getComponent: () => AssetSearch,
            },
        },
    ];

    getQualificationSelectList: () => Array<QualificationLevelSelectList> = () => [
        {
            alias: CommonRouteAliases.product,
            title: computedText(useProductNames("plural")),
            component: {
                getComponent: () => ProductSelectionForQualification,
            },
        },
        {
            alias: CommonRouteAliases.asset,
            title: computedText(useAssetNames("plural")),
            component: {
                getComponent: () => AssetSelectionForQualification,
            },
        },
    ];
}
